import { Address } from "@msuite/ui/address";
import { localized } from "@msuite/ui/localization";
import { createColumnHelper, createRowActions } from "@msuite/ui/table";
import { HintTooltip } from "@msuite/ui/tooltip";
import type { SubstitutionPlanFormItemSchema } from "@msuite/utils/schemas";
import { getAddressString, guardString } from "@msuite/utils/strings";
import { ProjectClient } from "@/components/projects";
import { RowActions } from "./row-actions";
import { SubstitutionPlanHints } from "./substitution-plan-hints";
import { SubstitutionPlanIsRequiredHandler } from "./substitution-plan-is-required-handler";
import { SubstitutionPlanNotifyContactsSelect } from "./substitution-plan-notifiy-contacts-select";
import { SubstitutionPlanQuickNote } from "./substitution-plan-quick-note";
import { SubstitutionPlanSelect } from "./substitution-plan-select";

const columnHelper = createColumnHelper<SubstitutionPlanFormItemSchema>();

export const columns = [
	columnHelper.accessor("projectNumber", {
		id: "projectNumber",
		header: localized("projects.fields.number"),
		cell: (cell) => guardString(cell.getValue()),
	}),
	columnHelper.accessor("projectName", {
		id: "projectName",
		header: localized("projects.fields.project_name"),
		cell: (cell) => cell.getValue(),
	}),
	columnHelper.accessor((row) => getAddressString(row), {
		id: "address",
		header: localized("address.address"),
		cell: (cell) => <Address item={cell.row.original} />,
		size: 300,
	}),
	columnHelper.accessor((row) => guardString(row.clientName), {
		id: "clientName",
		header: localized("projects.fields.client"),
		cell: (cell) => (
			<ProjectClient
				client={{ name: cell.row.original.clientName, id: cell.row.original.clientId }}
				fallback={guardString(null)}
			/>
		),
		size: 300,
	}),
	columnHelper.display({
		id: "substitution-picker",
		header: localized("substitutions.substitutions"),
		cell: (cell) => {
			return <SubstitutionPlanSelect itemId={cell.row.original.id} />;
		},
		size: 250,
	}),
	columnHelper.display({
		id: "notify-contacts-picker",
		header: localized("substitutions.notify_contacts"),
		cell: (cell) => {
			if (cell.row.original.isAssignmentNotRequired) return guardString(null);
			return (
				<SubstitutionPlanNotifyContactsSelect
					itemId={cell.row.original.id}
					projectId={cell.row.original.projectId}
				/>
			);
		},
		size: 250,
	}),
	columnHelper.accessor((row) => row.isAssignmentNotRequired, {
		id: "is-assignment-not-required",
		header: localized("general.not_required"),
		cell: (cell) => {
			return <SubstitutionPlanIsRequiredHandler itemId={cell.row.original.id} />;
		},
		size: 150,
	}),
	columnHelper.display({
		id: "quick-notes",
		header: () => (
			<div className="h-stack">
				<p>{localized("notes.quick_note")}</p>
				<HintTooltip label={localized("substitutions.this_hint_is_only_internally_visible")} />
			</div>
		),
		cell: (cell) => <SubstitutionPlanQuickNote itemId={cell.row.original.id} />,
		size: 250,
	}),
	columnHelper.display({
		id: "hints",
		header: localized("hints.hint"),
		cell: (cell) => {
			return <SubstitutionPlanHints itemId={cell.row.original.id} />;
		},
		size: 150,
	}),
	createRowActions(RowActions),
];
